/**
 * API Configuration
 * 
 * SECURITY FEATURES:
 * - httpOnly cookie authentication (tokens not accessible to JavaScript)
 * - CSRF token protection on all state-changing requests
 * - Automatic 401 handling with session refresh
 */

import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosError } from 'axios'
import { env } from '../config/env'
import { windowEvents } from '../constants/events'
import { isReauthRequired, requestReauth } from './reauth'

// Use Vite's proxy in dev, or explicit URL in production
const API_BASE_URL = env.API_URL

// Create axios instance
export const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 120000, // 2 minutes - increased for bulk sync operations
  withCredentials: true // CRITICAL: Send httpOnly cookies with requests
})

// -----------------------------------------------------------------------------
// Idempotency keys for state-changing POS / stock requests
//
// A sale, return or stock change that times out here may still have been applied on the
// server; sending it again used to apply it twice. Requests carrying an `Idempotency-Key`
// are answered from the first response when repeated (backend middleware/idempotency.ts).
//
// - Callers that let the user retry one action (Complete, a return) pass the SAME key for every
//   attempt: `api.post(url, body, withIdempotencyKey(key))`.
// - Every other write to the POS/stock endpoints below gets a fresh key automatically, which
//   still covers this client re-sending the same request object (CSRF / session refresh).
// -----------------------------------------------------------------------------

export const IDEMPOTENCY_HEADER = 'Idempotency-Key'

const WRITE_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE']

/** Paths (relative to the API base) whose writes move stock or money at the till. */
const IDEMPOTENT_WRITE_PATHS = [/^\/?trades(\/|$)/i, /^\/?transactions(\/|$)/i, /^\/?inventory\/[^/]+\/quantity$/i]

/** A new random key, unique per logical action. */
export function createIdempotencyKey(): string {
  const cryptoApi = typeof globalThis !== 'undefined' ? (globalThis.crypto as Crypto | undefined) : undefined
  if (cryptoApi?.randomUUID) return cryptoApi.randomUUID()
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`
}

/** Request config that sends `key` as the Idempotency-Key. */
export function withIdempotencyKey(key: string): { headers: Record<string, string> } {
  return { headers: { [IDEMPOTENCY_HEADER]: key } }
}

/** True for a write to one of the POS/stock endpoints that should always carry a key. */
export function needsIdempotencyKey(method: string | undefined, url: string | undefined): boolean {
  if (!method || !WRITE_METHODS.includes(method.toUpperCase())) return false
  const path = (url || '').split('?')[0].replace(/^https?:\/\/[^/]+/i, '').replace(/^\/api(?=\/)/i, '')
  return IDEMPOTENT_WRITE_PATHS.some((pattern) => pattern.test(path))
}

const isWriteRequest = (config: { method?: string } | undefined): boolean =>
  !!config?.method && WRITE_METHODS.includes(config.method.toUpperCase())

// Store CSRF token in memory (not localStorage for security)
let csrfToken: string | null = null

// Refresh-token mutex: ensures only ONE refresh call runs at a time.
// Concurrent 401 responses all wait for the same refresh promise instead of
// each issuing their own /auth/refresh call (which would rotate the token
// out from under the others and cause spurious sign-outs).
let refreshPromise: Promise<void> | null = null

// -----------------------------------------------------------------------------
// Lightweight request dedupe + short-lived cache for GET requests
//
// How this layers with react-query (so nobody debugs the wrong cache):
// - Dedupe (default ON): identical CONCURRENT GETs share one network call.
//   Never serves stale data — it only collapses in-flight duplicates.
//   Opt out per request with the X-Client-No-Dedupe header.
// - TTL cache (default OFF): serves a cached response for up to
//   X-Client-Cache-Ttl ms. Opt-in ONLY, via the X-Client-Cache header.
//   Used solely by polling endpoints (announcements, support unread counts)
//   that fire outside react-query. Everything else should rely on
//   react-query's staleTime — do not add new X-Client-Cache call sites
//   without a reason react-query can't cover.
// -----------------------------------------------------------------------------

type InflightPromise = Promise<any>

const DEFAULT_GET_CACHE_TTL_MS = 3_000
const MAX_CACHE_ENTRIES = 200

const inflightGetRequests = new Map<string, InflightPromise>()
const getResponseCache = new Map<string, { expiresAt: number; response: any }>()

const stableStringify = (value: unknown): string => {
  try {
    return JSON.stringify(value, Object.keys(value as any).sort())
  } catch {
    return String(value)
  }
}

const getCacheKey = (config: InternalAxiosRequestConfig): string => {
  const base = (config.baseURL || API_BASE_URL || '').toLowerCase()
  const url = (config.url || '').toLowerCase()
  const params = config.params ? stableStringify(config.params) : ''
  return `${base}${url}?${params}`
}

const getClientCacheTtlMs = (config: InternalAxiosRequestConfig): number | null => {
  const headers = (config.headers || {}) as any
  const enabled = headers['X-Client-Cache'] === true || headers['X-Client-Cache'] === 'true'
  if (!enabled) return null

  const ttlHeader = headers['X-Client-Cache-Ttl']
  if (typeof ttlHeader === 'number' && Number.isFinite(ttlHeader)) {
    return Math.max(0, ttlHeader)
  }
  if (typeof ttlHeader === 'string') {
    const parsed = parseInt(ttlHeader, 10)
    if (Number.isFinite(parsed)) return Math.max(0, parsed)
  }

  return DEFAULT_GET_CACHE_TTL_MS
}

const shouldSkipDedupe = (config: InternalAxiosRequestConfig): boolean => {
  const headers = (config.headers || {}) as any
  return headers['X-Client-No-Dedupe'] === true || headers['X-Client-No-Dedupe'] === 'true'
}

const shouldSkipAuthRedirect = (config: InternalAxiosRequestConfig): boolean => {
  const headers = (config.headers || {}) as any
  return headers['X-Auth-No-Redirect'] === true || headers['X-Auth-No-Redirect'] === 'true'
}

const pruneCacheIfNeeded = (): void => {
  if (getResponseCache.size <= MAX_CACHE_ENTRIES) return

  const now = Date.now()
  for (const [key, entry] of getResponseCache.entries()) {
    if (entry.expiresAt <= now) {
      getResponseCache.delete(key)
    }
  }

  // If still too large, drop oldest-ish by insertion order.
  while (getResponseCache.size > MAX_CACHE_ENTRIES) {
    const firstKey = getResponseCache.keys().next().value
    if (!firstKey) break
    getResponseCache.delete(firstKey)
  }
}

// Auth endpoints that should never trigger a refresh attempt
const AUTH_ENDPOINTS = ['/auth/login', '/auth/verify', '/auth/refresh']

const getRequestUrl = (config: InternalAxiosRequestConfig): string => {
  const base = config.baseURL || ''
  const url = config.url || ''
  return `${base}${url}`.toLowerCase()
}

/**
 * Fetch CSRF token from backend
 * Should be called on app initialization
 */
export const getCsrfToken = async (): Promise<string> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/csrf-token`, {
      withCredentials: true
    })
    
    csrfToken = response.data.csrfToken
    return csrfToken || ''
  } catch (error) {
    console.error('Failed to fetch CSRF token:', error)
    throw error
  }
}

/**
 * Request Interceptor
 * Automatically attach CSRF token to state-changing requests
 */
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Dedupe + short cache for GET requests to reduce bursty re-fetching
    const method = config.method?.toUpperCase()
    if (method === 'GET') {
      const cacheKey = getCacheKey(config)

      const cacheTtlMs = getClientCacheTtlMs(config)
      if (cacheTtlMs && cacheTtlMs > 0) {
        const cached = getResponseCache.get(cacheKey)
        if (cached && cached.expiresAt > Date.now()) {
          const cachedResponse = cached.response
          config.adapter = async () => cachedResponse
          return config
        }
      }

      // Already cancelled: axios will refuse to send it; nothing to share.
      if (!shouldSkipDedupe(config) && !(config as any).signal?.aborted) {
        const adapter = resolveAxiosAdapter((config as any).adapter ?? (api.defaults as any).adapter)
        if (!adapter) {
          throw new Error('[api] Unable to resolve Axios adapter')
        }

        const existing = inflightGetRequests.get(cacheKey)
        if (existing) {
          // Share the request already on its way — unless that one was cancelled by ITS caller,
          // which says nothing about this one: then send our own.
          config.adapter = async (cfg: any) => {
            try {
              return await existing
            } catch (e) {
              if (axios.isCancel?.(e) && !cfg.signal?.aborted) return adapter(cfg)
              throw e
            }
          }
          return config
        }

        let resolveInflight: ((value: any) => void) | undefined
        let rejectInflight: ((reason?: any) => void) | undefined
        const inflight = new Promise<any>((resolve, reject) => {
          resolveInflight = resolve
          rejectInflight = reject
        })
        // Nobody may be waiting on it; an unobserved rejection is not an error here.
        inflight.catch(() => undefined)

        inflightGetRequests.set(cacheKey, inflight)

        // Aborted before it was sent (axios checks the signal between this interceptor and the
        // adapter, so the adapter below never runs): settle and drop the entry. It used to stay
        // in the map unsettled, so every later identical GET waited on it forever — a React
        // Query refetch after a cancelled fetch (StrictMode's double mount, a search key typed
        // away from and back to) hung on "loading".
        let started = false
        const signal = (config as any).signal as AbortSignal | undefined
        signal?.addEventListener?.('abort', () => {
          if (started) return
          if (inflightGetRequests.get(cacheKey) === inflight) inflightGetRequests.delete(cacheKey)
          rejectInflight?.(new axios.CanceledError('canceled'))
        }, { once: true })

        config.adapter = async (cfg: any) => {
          started = true
          try {
            const response = await adapter(cfg)
            resolveInflight?.(response)
            return response
          } catch (e) {
            rejectInflight?.(e)
            throw e
          } finally {
            if (inflightGetRequests.get(cacheKey) === inflight) inflightGetRequests.delete(cacheKey)
          }
        }
      }
    }

    // Add CSRF token to state-changing requests
    if (method && WRITE_METHODS.includes(method)) {
      if (csrfToken && config.headers) {
        config.headers['X-CSRF-Token'] = csrfToken
      }
      // Kept on the config, so a re-send of this same request object reuses it.
      if (config.headers && !config.headers[IDEMPOTENCY_HEADER] && needsIdempotencyKey(method, config.url)) {
        config.headers[IDEMPOTENCY_HEADER] = createIdempotencyKey()
      }
    }

    // If the app is running embedded inside Shopify Admin, tag requests so the backend
    // can hard-disable any off-platform billing flows (e.g. Stripe).
    try {
      const params = new URLSearchParams(window.location.search)
      const hasShopParams = !!(params.get('shop') && (params.get('host') || params.get('hmac') || params.get('embedded') || params.get('session')))

      let isIframe = false
      try {
        isIframe = window.top !== window
      } catch {
        // cross-origin access to window.top can throw; treat as iframe
        isIframe = true
      }

      const ref = String(document.referrer || '').toLowerCase()
      const fromShopifyAdmin = ref.includes('admin.shopify.com') || ref.includes('.myshopify.com')

      if ((hasShopParams || isIframe || fromShopifyAdmin) && config.headers) {
        config.headers['X-TCGEASY-SHOPIFY-EMBEDDED'] = '1'
      }
    } catch {
      // ignore
    }
    
    return config
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  }
)

// Axios v1 may expose defaults.adapter as an array or string selector.
// We must resolve it to a callable adapter function before invoking.
function resolveAxiosAdapter(adapter: any): ((cfg: any) => Promise<any>) | null {
  if (!adapter) return null
  if (typeof adapter === 'function') return adapter

  const getAdapter = (axios as any).getAdapter
  if (typeof getAdapter === 'function') {
    try {
      const resolved = getAdapter(adapter)
      if (typeof resolved === 'function') return resolved
    } catch {
      // ignore and fall through
    }
  }

  if (Array.isArray(adapter)) {
    const firstFn = adapter.find((a) => typeof a === 'function')
    if (firstFn) return firstFn
  }

  return null
}

/**
 * Response Interceptor
 * Handle 401 errors (session expired) and 403 errors (CSRF failure)
 */
/**
 * A sentence for a request that never reached the server, or never came back.
 *
 * Null for a cancellation — a request the app itself aborted is not a failure to report.
 */
export function describeTransportFailure(error: AxiosError): string | null {
  if (error.response) return null
  if (axios.isCancel?.(error)) return null
  if (error.code === 'ECONNABORTED' || /timeout/i.test(error.message || '')) {
    // A change that timed out may well have been made — the server keeps going. "Try again"
    // here is how a sale or return got applied twice.
    if (isWriteRequest(error.config)) return WRITE_OUTCOME_UNKNOWN
    return 'That request took too long and was stopped. Check your connection and try again.'
  }
  return 'Could not reach the server. Check your internet connection and try again.'
}

/** For a change whose answer never arrived: it may have gone through, so check first. */
export const WRITE_OUTCOME_UNKNOWN =
  'The server did not answer in time, so this change may or may not have gone through. Refresh to check before trying again.'

/**
 * Human wording for an HTTP status, used ONLY when the response carried no message of its own.
 *
 * Our API nearly always explains itself, so this is a backstop for the cases that do not: an
 * empty body, an HTML page from nginx, a proxy timeout. Without it those surface as axios's
 * "Request failed with status code 400", which reads like the app is broken and gives the user
 * nothing to act on.
 */
export function describeHttpStatus(status: number): string | null {
  if (status === 401) return 'Your session has expired. Please log in again.'
  if (status === 403) return 'You do not have permission to do that.'
  if (status === 404) return 'We could not find what you were looking for. It may have been moved or removed.'
  if (status === 409) return 'That clashed with a change made a moment ago. Refresh and try again.'
  if (status === 413) return 'That file is too large to upload. Try a smaller one.'
  if (status === 429) return 'That was a lot of requests at once. Please wait a moment and try again.'
  if (status === 503) return 'The server is temporarily unavailable. Please try again in a moment.'
  // Honest about whose fault it is: telling someone to check their own input on a 5xx would send
  // them hunting for a mistake they did not make.
  if (status >= 500) {
    return 'The server had a problem handling that request. Please try again in a moment — if it keeps happening, this one is on us.'
  }
  if (status >= 400) return 'That request was not accepted. Please check what you entered and try again.'
  return null
}

/** Non-blank string, or null. Blank is treated as absent everywhere below. */
function usableText(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null
}

/**
 * Guarantee every rejected request carries a message a person can read and act on.
 *
 * Two independent gaps used to make an error arrive as either nothing or jargon:
 *
 *  1. The backend's `ResponseHelper.error()` — which every badRequest/notFound/forbidden funnels
 *     into — puts its human text in `error` and omits `message` entirely, while ~100 call sites
 *     read `data.message`. They fell through to their own generic fallback and the real reason
 *     never reached the user.
 *  2. When there is no server message at all (network dropped, timeout, an HTML 502 from nginx),
 *     call sites fall back to `error.message`, which axios fills with "Network Error" or
 *     "Request failed with status code 500" — jargon that reads like the app is broken.
 *
 * Fixing it here rather than at the call sites means a new caller cannot reintroduce either gap.
 * The original axios text is kept on `originalMessage` so diagnostics lose nothing.
 */
export function normaliseErrorMessages(error: AxiosError): void {
  const data = error.response?.data
  const payload =
    data && typeof data === 'object' && !Array.isArray(data)
      ? (data as Record<string, unknown>)
      : null

  if (payload && !usableText(payload.message) && usableText(payload.error)) {
    payload.message = payload.error
  }

  // Still nothing a person could read? Fill it in from the transport or the status code.
  if (payload && usableText(payload.message)) return

  // A gateway timeout on a write says nothing about whether the write happened.
  const status = error.response?.status
  const writeOutcomeUnknown = (status === 408 || status === 504) && isWriteRequest(error.config)
  const humanFallback = writeOutcomeUnknown
    ? WRITE_OUTCOME_UNKNOWN
    : error.response
      ? describeHttpStatus(error.response.status)
      : describeTransportFailure(error)
  if (!humanFallback) return

  if (payload) payload.message = humanFallback
  ;(error as AxiosError & { originalMessage?: string }).originalMessage = error.message
  error.message = humanFallback
}

api.interceptors.response.use(
  (response: any) => {
    // Cache GET responses briefly (safe, in-memory only) to reduce duplicate loads
    const method = response?.config?.method?.toUpperCase?.()
    if (method === 'GET') {
      const config = response.config as InternalAxiosRequestConfig
      const cacheTtlMs = getClientCacheTtlMs(config)
      if (cacheTtlMs && cacheTtlMs > 0) {
        const cacheKey = getCacheKey(config)
        pruneCacheIfNeeded()
        getResponseCache.set(cacheKey, {
          expiresAt: Date.now() + cacheTtlMs,
          response
        })
      }
    }

    // Return successful response
    return response
  },
  async (error: AxiosError) => {
    normaliseErrorMessages(error)

    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean; _reauthRetry?: boolean }

    // If no config, reject immediately
    if (!originalRequest) {
      return Promise.reject(error)
    }

    // A big account action needs a fresh "confirm it's you" (utils/reauth.ts): prompt, then
    // retry once. Cancelling rejects with the server's own message.
    if (error.response?.status === 403 && isReauthRequired(error.response.data) && !originalRequest._reauthRetry) {
      originalRequest._reauthRetry = true
      const confirmed = await requestReauth({ mfaRequired: error.response.data.mfaRequired === true })
      if (confirmed) return api(originalRequest)
      return Promise.reject(error)
    }

    // Handle CSRF token errors (403)
    if (error.response?.status === 403) {
      const errorMessage = (error.response.data as any)?.message || ''
      
      if (errorMessage.toLowerCase().includes('csrf')) {
        // CSRF token invalid - fetch new one and retry
        if (!originalRequest._retry) {
          originalRequest._retry = true
          
          try {
            await getCsrfToken()
            
            // Retry the request with new CSRF token
            if (csrfToken && originalRequest.headers) {
              originalRequest.headers['X-CSRF-Token'] = csrfToken
            }
            
            return api(originalRequest)
          } catch (csrfError) {
            console.error('Failed to refresh CSRF token:', csrfError)
            return Promise.reject(error)
          }
        }
      }
    }

    // Handle authentication errors (401)
    if (error.response?.status === 401) {
      const requestUrl = getRequestUrl(originalRequest)
      const shouldSkipRefresh = AUTH_ENDPOINTS.some((endpoint) => requestUrl.includes(endpoint))

      // NEVER retry auth endpoints to prevent infinite loops
      if (shouldSkipRefresh) {
        return Promise.reject(error)
      }

      // If already retried, don't try again - prevents infinite loop
      if (originalRequest._retry) {
        return Promise.reject(error)
      }

      // Try to refresh the session using refresh token cookie.
      // Use a mutex so concurrent 401s share a single refresh call instead of
      // racing and rotating the token out from under each other.
      originalRequest._retry = true

      try {
        if (!refreshPromise) {
          refreshPromise = api.post('/auth/refresh', {}).then(
            () => { refreshPromise = null },
            (err) => { refreshPromise = null; throw err }
          )
        }
        await refreshPromise

        // Retry the original request
        return api(originalRequest)
      } catch (refreshError) {
        // If refresh fails, the session is invalid OR cookies aren't being sent.
        // Redirect to login so the UI doesn't remain in a broken "authenticated" state.
        try {
          if (typeof window !== 'undefined' && !shouldSkipAuthRedirect(originalRequest)) {
            const onLoginPage = window.location.pathname.startsWith('/login')
            if (!onLoginPage) {
              const next = `${window.location.pathname}${window.location.search}${window.location.hash}`
              const loginUrl = `/login?next=${encodeURIComponent(next)}`
              if (window.top && window.top !== window) {
                window.top.location.href = loginUrl
              } else {
                window.location.href = loginUrl
              }
            }
          }
        } catch {
          // ignore navigation errors
        }
        // Refresh failed - reject without retrying
        return Promise.reject(refreshError)
      }
    }

    // Handle rate limit (429) — dispatch an event so the UI can show a banner
    if (error.response?.status === 429) {
      const data = error.response.data as any
      const detail: RateLimitDetail = {
        message: data?.message || data?.error || 'You have exceeded your API usage limit. Please try again later.',
        limit: data?.limit,
      }
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(RATE_LIMIT_EVENT, { detail }))
      }
    }

    return Promise.reject(error)
  }
)

/**
 * Rate limit event — dispatched on 429 responses so the UI can show a banner.
 * Payload: { message: string, limit?: { current: number, max: number, resetsAt: string } }
 */
export const RATE_LIMIT_EVENT = windowEvents.rateLimit

export interface RateLimitDetail {
  message: string
  limit?: { current: number; max: number; resetsAt: string }
}

/**
 * Helper function to handle API errors
 */
export const getErrorMessage = (error: any): string => {
  if (axios.isAxiosError(error)) {
    // Network error (can't reach server)
    if (error.code === 'ERR_NETWORK' || !error.response) {
      return `Cannot connect to server. Please ensure the backend is running on ${API_BASE_URL}`
    }
    
    const status = error.response?.status
    
    // For 429, build a detailed message including limit info when available
    if (status === 429) {
      const data = error.response?.data as any
      if (data?.message) return data.message
      if (data?.error) return data.error
      return 'You have exceeded your API usage limit. Please try again later or upgrade your plan.'
    }

    // ALWAYS prioritize server's error message if available
    if (error.response?.data?.error) {
      return error.response.data.error
    }
    if (error.response?.data?.message) {
      return error.response.data.message
    }

    // Fallback messages for specific status codes (only if server didn't provide a message)
    if (status === 401 || status === 403) {
      return 'Invalid email or password. Please check your credentials and try again.'
    }
    
    if (status && status >= 500) {
      return 'Unable to connect. Please try again in a moment.'
    }
    
    // Other errors
    if (error.message) {
      return error.message
    }
  }
  
  return 'An unexpected error occurred'
}

export default api
